import React from 'react';
import { Link } from 'react-router-dom';

export const TermsPage: React.FC = () => {
  return (
    <div>
      <section
        className="text-white text-center py-16 px-4"
        style={{ background: 'linear-gradient(135deg, #2c5530 0%, #4a7c59 100%)' }}
      >
        <h1 className="text-4xl font-semibold mb-3">Terms of Service</h1>
        <p className="text-lg opacity-90">A binding agreement between you and Pop-Up Yoga</p>
      </section>

      <section className="bg-white py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 text-sm text-yellow-800">
            <strong>Draft — not yet in effect.</strong> This is a working draft pending attorney review — particularly the
            liability release, arbitration clause, and independent-contractor language. Placeholders (entity name, effective
            date, contact address, arbitration administrator) will be filled in before these Terms take effect.
          </div>

          <p className="text-gray-500 text-sm mb-6"><strong>Effective Date:</strong> [EFFECTIVE DATE]</p>

          <p className="text-gray-700 mb-8">
            These Terms of Service ("<strong>Terms</strong>") are a binding agreement between you and{' '}
            <strong>[Pop-Up Yoga LLC, a Virginia limited liability company]</strong> ("<strong>Pop-Up Yoga</strong>")
            (<em>entity formation pending</em>) governing your access to and use of the Pop-Up Yoga website, applications,
            and related services (the "<strong>Platform</strong>"). By creating an account, registering for an event, or
            otherwise using the Platform, you agree to these Terms.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">1. What Pop-Up Yoga Is — and Isn't</h2>
          <p className="text-gray-700 mb-4">
            Pop-Up Yoga connects students ("Yogis") with independent yoga teachers ("Leaders") who organize and lead
            yoga classes and events ("Events"), and supports people who help organize Events on behalf of a Leader ("Organizers").
          </p>
          <p className="text-gray-700 mb-8">
            <strong>Pop-Up Yoga does not employ, supervise, train, certify, or control Leaders</strong>, and does not itself
            teach or provide any yoga instruction. The quality, safety, and conduct of any Event is the responsibility of
            the Leader running it and the Yogis attending it — not Pop-Up Yoga.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">2. Eligibility</h2>
          <p className="text-gray-700 mb-8">
            You must be at least <strong>18 years old</strong> to create an account or use the Platform. By registering,
            you represent that you meet this requirement and that all information you provide is accurate.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">3. Accounts and Roles</h2>
          <p className="text-gray-700 mb-2">
            <strong>3.1 Account creation.</strong> You must provide a valid email address and, where requested, a phone
            number to register. These are used to verify you are a real person and to enable essential communication — they
            are not shared with other users except as described in our <Link to="/privacy" className="text-green-700 hover:underline">Privacy Policy</Link>.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>3.2 Roles.</strong> The Platform supports Yogi, Leader, Organizer, and Admin roles. Role upgrades
            (e.g., requesting Leader status) may require additional information and administrator approval.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>3.3 Accuracy and security.</strong> You are responsible for keeping your account information accurate and
            safeguarding your login credentials.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>3.4 One person, one account.</strong> Accounts are personal to you and may not be shared, sold, or transferred.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">4. Community Standards</h2>
          <p className="text-gray-700 mb-3">Everyone using Pop-Up Yoga agrees to:</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
            <li>Treat other users with the respect appropriate to a shared practice space</li>
            <li>Provide accurate information about yourself, your credentials (if a Leader), and any Event you create</li>
            <li>Not use the Platform to harass, discriminate against, defraud, or endanger others</li>
            <li>Not attempt to circumvent the privacy protections (e.g., scraping contact information, impersonating other users)</li>
            <li>Not use the Platform for purposes unrelated to yoga practice, instruction, or community-building</li>
          </ul>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">5. Events</h2>
          <p className="text-gray-700 mb-2">
            <strong>5.1</strong> Leaders and Organizers may create Events; Yogis may register for them, subject to capacity
            and any waitlist functionality. Please cancel promptly if your plans change so a waitlisted Yogi can take your spot.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>5.2</strong> Leaders and Organizers are responsible for communicating changes or cancellations to
            registered attendees through the Platform's notification tools.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>5.3</strong> Pop-Up Yoga does not guarantee that any Event will occur, meet any particular standard, or
            be suitable for your skill level or physical condition.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">6. Fees — Current and Future</h2>
          <p className="text-gray-700 mb-2">
            <strong>6.1 Free, today.</strong> All use of the Platform — searching for Events, registering as a Yogi, and
            creating or leading free Events as a Leader — is free of charge. Finding and offering free yoga through Pop-Up
            Yoga will always be free.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>6.2 Planned future monetization.</strong> We plan to introduce donation-supported Events and paid Events
            with a transparent per-student platform fee. These Terms will be updated before either feature becomes available,
            and you will have the opportunity to review those terms first.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>6.3 No payment processing today.</strong> Pop-Up Yoga does not currently process payments or store
            payment information.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">7. Assumption of Risk; Release of Liability</h2>
          <p className="text-gray-700 font-semibold mb-4">PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.</p>
          <p className="text-gray-700 mb-2">
            <strong>7.1 Inherent risk.</strong> Yoga and related physical activity involve inherent risks, including muscle
            strains, sprains, falls, cardiovascular events, aggravation of pre-existing conditions, and other injury.
            <strong> You voluntarily assume all risks associated with attending, leading, or otherwise participating in any
            Event found through the Platform</strong>, whether the risk is known or unknown to you at the time.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>7.2 Your responsibility.</strong> You are solely responsible for assessing your own physical condition and
            fitness, consulting a physician if appropriate, informing the Leader of any relevant limitation, and following the
            Leader's instructions and your own judgment during any Event.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>7.3 Release.</strong> To the maximum extent permitted by applicable law, you release and discharge
            Pop-Up Yoga, its officers, employees, and agents from any liability, claims, or causes of action arising from
            your participation in any Event — <strong>except to the extent caused by Pop-Up Yoga's own gross negligence
            or willful misconduct in operating the Platform itself</strong>.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>7.4 No release of Leaders.</strong> Nothing in these Terms releases a Leader from their own responsibility
            to the Yogis attending their Events.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>7.5</strong> Some jurisdictions limit liability releases. If any part of this release is found
            unenforceable in your jurisdiction, the rest remains in effect to the fullest extent permitted.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">8. Privacy</h2>
          <p className="text-gray-700 mb-8">
            We collect only the information necessary to operate the Platform, as described in our{' '}
            <Link to="/privacy" className="text-green-700 hover:underline">Privacy Policy</Link>. Your email and phone number
            are used for account verification and essential communication — they are not visible to other Yogis, Leaders,
            or Organizers. Event communication between a Leader and registered attendees is facilitated through the Platform
            without exposing personal contact information.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">9. Communications</h2>
          <p className="text-gray-700 mb-8">
            By creating an account, you agree to receive communications necessary to the operation of the Platform (e.g.,
            registration confirmations, event changes, security notices). You may opt out of non-essential communications
            (such as regional newsletters) at any time through your account settings.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">10. Intellectual Property; Your Content</h2>
          <p className="text-gray-700 mb-2">
            <strong>10.1</strong> The Platform, its design, and its underlying software are owned by Pop-Up Yoga or its
            licensors.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>10.2</strong> You retain ownership of content you submit (profile information, bios, Event descriptions).
            By submitting content, you grant Pop-Up Yoga a non-exclusive, royalty-free license to display, store, and
            distribute that content as necessary to operate the Platform.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>10.3</strong> If you believe content on the Platform infringes your intellectual property rights,
            contact us at [LEGAL CONTACT EMAIL].
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">11. Leader Relationship</h2>
          <p className="text-gray-700 mb-2">
            <strong>11.1 Independent contractors.</strong> Leaders are <strong>independent third parties, not employees,
            agents, partners, or representatives of Pop-Up Yoga</strong>. A Leader's actions and the content of their Events
            are their own and are not endorsed, supervised, or controlled by Pop-Up Yoga.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>11.2 Credentials.</strong> Leaders are solely responsible for the accuracy of any certification or
            experience they represent. Pop-Up Yoga's review of submitted credentials is a good-faith review of provided
            information, not an independent verification or guarantee of a Leader's qualifications.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>11.3 Insurance.</strong> Pop-Up Yoga strongly recommends that Leaders maintain their own general
            liability and/or professional liability insurance. This is a recommendation, not a platform requirement.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>11.4</strong> Nothing in these Terms creates an employment, agency, partnership, or joint-venture
            relationship between Pop-Up Yoga and any Leader.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">12. Disclaimers</h2>
          <p className="text-gray-700 mb-8 uppercase text-sm leading-relaxed">
            The Platform is provided "as is" and "as available," without warranties of any kind, whether express or implied,
            including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            We do not warrant that the Platform will be uninterrupted, error-free, or secure, or that any Event found
            through the Platform will meet your expectations or be safe for your participation.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">13. Limitation of Liability</h2>
          <p className="text-gray-700 mb-8 uppercase text-sm leading-relaxed">
            To the maximum extent permitted by law, Pop-Up Yoga's total liability to you for any claim arising out of or
            relating to these Terms or the Platform shall not exceed the greater of (a) the amount you paid to Pop-Up Yoga
            in the twelve months preceding the claim, or (b) one hundred U.S. dollars ($100). Pop-Up Yoga shall not be
            liable for any indirect, incidental, special, consequential, or punitive damages.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">14. Suspension and Termination</h2>
          <p className="text-gray-700 mb-8">
            We may suspend or terminate your account if you violate these Terms or if we reasonably believe your account
            poses a risk to the Platform or other users. You may stop using the Platform and request account deletion at
            any time through your profile settings or by contacting us. Sections 7, 12, 13, 15, and 16 survive termination.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">15. Indemnification</h2>
          <p className="text-gray-700 mb-8">
            You agree to indemnify and hold Pop-Up Yoga harmless from any claims, damages, or expenses (including reasonable
            attorneys' fees) arising from your violation of these Terms, your use of the Platform, or — if you are a Leader
            — your conduct of any Event, except to the extent caused by Pop-Up Yoga's own breach of these Terms or gross negligence.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">16. Dispute Resolution; Binding Arbitration</h2>
          <p className="text-gray-700 font-semibold mb-4">
            PLEASE READ THIS SECTION CAREFULLY — IT REQUIRES YOU TO ARBITRATE DISPUTES AND LIMITS HOW YOU MAY SEEK RELIEF.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>16.1</strong> Before filing any claim, contact us at [LEGAL CONTACT EMAIL] and attempt to resolve the
            dispute informally for at least 30 days.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>16.2</strong> If informal resolution fails, disputes will be resolved by binding arbitration administered
            by [ARBITRATION ADMINISTRATOR] rather than in court, except for individual claims in small claims court.
          </p>
          <p className="text-gray-700 mb-2">
            <strong>16.3 No class actions.</strong> Any arbitration or proceeding will be conducted only on an individual
            basis and not as a class, consolidated, or representative action.
          </p>
          <p className="text-gray-700 mb-8">
            <strong>16.4 Right to opt out.</strong> You may opt out of this arbitration agreement by sending written notice
            to [LEGAL CONTACT EMAIL] within 30 days of first accepting these Terms.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">17. Governing Law</h2>
          <p className="text-gray-700 mb-8">
            These Terms are governed by the laws of the Commonwealth of Virginia. Where a dispute is not subject to
            arbitration, the parties consent to the exclusive jurisdiction of the state and federal courts located in Virginia.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">18. Changes to These Terms</h2>
          <p className="text-gray-700 mb-8">
            We may update these Terms from time to time. If we make material changes, we will provide notice before the
            changes take effect. Continued use of the Platform after changes take effect constitutes acceptance of the
            updated Terms.
          </p>

          <h2 className="text-2xl font-semibold text-green-800 mt-8 mb-4">19. Miscellaneous</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
            <li><strong>Entire agreement.</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and Pop-Up Yoga regarding the Platform.</li>
            <li><strong>Severability.</strong> If any provision is found unenforceable, the remaining provisions remain in full effect.</li>
            <li><strong>No waiver.</strong> Our failure to enforce any provision is not a waiver of our right to do so later.</li>
            <li><strong>Assignment.</strong> You may not assign these Terms without our consent; we may assign them in connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>Contact.</strong> Questions about these Terms can be sent to [LEGAL CONTACT EMAIL].</li>
          </ul>
        </div>
      </section>

      <footer className="bg-gray-800 text-white text-center py-8">
        <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} Pop-Up Yoga. All rights reserved.</p>
        <p className="text-gray-500 text-xs mt-2">
          <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
          {' · '}
          <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
};
